import { useEffect, useState } from 'react';
import { ChatService } from '../../../libs/services/ChatService';
import { isEntraConfigured } from './entraConfig';

// localStorage cache of the last /info-resolved flag value. The flag is environment-wide (not tied
// to the per-tab Entra MSAL session in entraConfig), so caching it lets a repeat load on an Entra
// agent mount the Entra console immediately instead of blanking on the /info round-trip.
//
// IMPORTANT — only an eager TRUE may be trusted from the cache. Mounting EntraAgentAuthProvider early
// is safe: it just renders the Entra sign-in screen. Eagerly resolving to FALSE is NOT safe, because
// AgentEntry (index.tsx) then mounts the Visma OIDC path whose AuthBootstrap immediately redirects to
// Visma Connect (a full navigation). A stale cached `false` during the flag OFF->ON rollout — every
// agent that loaded /agents while the flag was off has `'0'` cached — would bounce an Entra-eligible
// agent to Visma Connect before /info can correct it, and the navigation tears down the revalidation
// so they can get stuck there. So a cached/absent non-true value stays `undefined` (AgentEntry renders
// null briefly) until /info confirms.
const FLAG_CACHE_KEY = 'livechat.agent.entraLoginEnabled';

const readCachedFlagIsTrue = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        return window.localStorage.getItem(FLAG_CACHE_KEY) === '1';
    } catch {
        // localStorage may be blocked (private mode); behave as if uncached.
        return false;
    }
};

const writeCachedFlag = (value: boolean): void => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(FLAG_CACHE_KEY, value ? '1' : '0');
    } catch {
        // localStorage may be blocked; the in-memory state still drives this load.
    }
};

// Whether the agent console should authenticate via Microsoft Entra ID (#1994) instead of the
// shared Visma Online OIDC session.
//
// This flag uniquely has to be known BEFORE the agent authenticates — it decides *how* to
// authenticate — so it cannot come from the post-auth GET /livechat/agent/me path the other agent
// flags use. /info is anonymous and carries the exposeToFrontend feature flags, so we fetch it here
// with an empty token. On any failure we keep an eager-true seed (or fall back to `false`, the
// current Visma Online behaviour), so a flaky /info can never lock agents out of the console.
//
// When the build carries no Entra coordinates, Entra mode can never activate (AgentEntry also gates
// on isEntraConfigured()), so we skip the /info round-trip entirely and resolve to `false`
// synchronously. When coordinates ARE baked in (every deployed build once REACT_APP_ENTRA_* is
// wired) we seed Entra (true) from the cache when it is known so a returning Entra agent mounts
// without a blank; any non-true cache stays `undefined` and waits for /info (see the cache note
// above on why we must never eagerly mount the Visma redirect path).
//
// Returns undefined while the flag is still being resolved, then the boolean.
export const useAgentEntraLoginFlag = (): boolean | undefined => {
    const [enabled, setEnabled] = useState<boolean | undefined>(() => {
        if (!isEntraConfigured()) return false;
        // Eager Entra only; otherwise wait for /info rather than risk the Visma redirect.
        return readCachedFlagIsTrue() ? true : undefined;
    });

    useEffect(() => {
        if (!isEntraConfigured()) {
            return undefined;
        }
        let cancelled = false;
        const load = async (): Promise<void> => {
            try {
                const info = await new ChatService('').getServiceInfoAsync();
                if (cancelled) return;
                // featureFlags is typed Record<string, boolean>, but treat a missing key (older
                // backend) as off rather than letting `undefined` read as "still loading".
                const flags = info.featureFlags as Record<string, boolean | undefined>;
                const value = flags.LiveChatAgentEntraLoginEnabled ?? false;
                writeCachedFlag(value);
                setEnabled(value);
            } catch {
                if (cancelled) return;
                // Keep an eager-true seed on a transient failure; an unresolved load falls back to
                // false, so a flaky /info never locks agents out of the console.
                setEnabled((prev) => prev ?? false);
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, []);

    return enabled;
};
